import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, Link } from 'react-router-dom';

import { useGetAgreementPartiesQuery, useGetBuildingsByIdQuery, useGetSpacesByIdQuery } from '@client/api';
import { CompaniesPaths } from '../../../companies/companies.router';
import { useTenants } from '../../../../hooks/useTenants/useTenants';
import { TenantSelectionEnum } from '../../../../redux';
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyPlaceholder,
  Pagination,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
} from '@client/toolkit';


export interface BuildingAgreementPartiesCardProps {
  className?: string;
  buildingId?: string;
  spaceId?: never;
}

export interface SpaceAgreementPartiesCardProps {
  className?: string;
  spaceId?: string;
  buildingId?: never;
}

export type AgreementPartiesCardProps = BuildingAgreementPartiesCardProps | SpaceAgreementPartiesCardProps;

export function AgreementPartiesCard({
  className,
  spaceId: parentSpaceId,
  buildingId: parentBuildingId,
}: AgreementPartiesCardProps) {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');
  const { selectedTenant } = useTenants();
  const currentTenantId = selectedTenant && selectedTenant !== TenantSelectionEnum.All ? selectedTenant.id : null;

  const pageSize = 25;
  const [page, setPage] = useState(0);

  const {
    data: space,
    isLoading: isLoadingSpace,
  } = useGetSpacesByIdQuery(
    {
      id: parentSpaceId ?? '',
    },
    { skip: !parentSpaceId },
  );

  const {
    data: building,
    isLoading: isLoadingBuilding,
  } = useGetBuildingsByIdQuery(
    {
      id: parentBuildingId ?? '',
    },
    { skip: !parentBuildingId },
  );

  const ancestorPath = useMemo(() => space?.path || building?.path, [building?.path, space?.path]);

  const {
    data: agreementPartiesData,
    isLoading: isLoadingAgreementParties,
  } = useGetAgreementPartiesQuery(
    {
      'spaceAncestorPath.Equal': ancestorPath,
      'pagination.Limit': pageSize,
      'pagination.Offset': page * pageSize,
    },
    { skip: !ancestorPath },
  );

  // Filter to only show tenants (type === 'Tenant' from Role enum)
  const agreementParties = useMemo(
    () => (agreementPartiesData?.items ?? []).filter((party) => party.type === 'Tenant'),
    [agreementPartiesData?.items],
  );

  const isListEmpty = useMemo(() => agreementParties != null && agreementParties.length <= 0, [agreementParties]);
  const isLoading = isLoadingAgreementParties || isLoadingBuilding || isLoadingSpace;

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex-1" variant="dense">
          {tGlobal('entity.tenant', { context: 'plural' })}
        </CardTitle>
        <CardActions />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-full w-full" />
        ) : (
          <Card>
            <Table className="rounded-md overflow-hidden">
              <TableHead>
                <TableHeadRow>
                  <TableHeadCell className="rounded-tl-md w-[80%]">{tGlobal('company.name')}</TableHeadCell>
                  <TableHeadCell className="rounded-tr-md">{tGlobal('company.role')}</TableHeadCell>
                </TableHeadRow>
              </TableHead>

              <TableBody>
                {!isLoading && isListEmpty && (
                  <TableRow>
                    <TableCell colSpan={2} className="p-4">
                      <EmptyPlaceholder fullWidth>
                        {tCommon('noEntityFound', {
                          entity: tGlobal('entity.tenant', { context: 'plural' }).toLowerCase(),
                        })}
                      </EmptyPlaceholder>
                    </TableCell>
                  </TableRow>
                )}
                {agreementParties &&
                  agreementParties.length > 0 &&
                  agreementParties.map((agreementParty) => (
                    <TableRow key={agreementParty.id}>
                      <TableCell>
                        {agreementParty.stakeholder?.id === currentTenantId ? (
                          <Link
                            to="/profile"
                            className="hover:text-brand-primary transition-colors"
                          >
                            {agreementParty.stakeholder?.name}
                          </Link>
                        ) : agreementParty.stakeholder?.companyId ? (
                          <Link
                            to={generatePath(CompaniesPaths.Company, { id: agreementParty.stakeholder.companyId })}
                            className="hover:text-brand-primary transition-colors"
                          >
                            {agreementParty.stakeholder?.name}
                          </Link>
                        ) : (
                          agreementParty.stakeholder?.name
                        )}
                      </TableCell>
                      <TableCell>{tType('RelationshipType.Tenant')}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            <Pagination totalCount={agreementParties.length} currentPage={page} setPage={setPage} pageSize={pageSize} />
          </Card>
        )}
      </CardContent>
    </Card>
  );
}
